import React, { KeyboardEvent } from "react";

import { resolveTwoLetterCode } from "../../helpers/language";
import { PlayerStoreProperties } from "../../store/store";
import { AudioTrack, Language, SelectedAudioTrack } from "../../types/common";
import {
  ChromecastServiceScheduleSubtitles,
  Subtitles
} from "../../types/player-api";

export const stopEventPropagation = (
  e:
    | React.FocusEvent
    | React.MouseEvent<HTMLDivElement, MouseEvent>
    | React.KeyboardEvent<HTMLButtonElement>
): void => e.stopPropagation();

export const handleKeyboardEvent = (e: KeyboardEvent): void => {
  if (e.key === " ") {
    e.stopPropagation();
  }
};

export const resolveBuffered = ({
  buffered,
  currentTime
}: {
  buffered?: TimeRanges;
  currentTime: number;
}): number => {
  if (!buffered) {
    return 0;
  }

  let closestBuffer = 0;

  for (let i = 0; i < buffered.length; i++) {
    const bufferedEnd = buffered.end(i);
    if (
      bufferedEnd > currentTime &&
      (closestBuffer === 0 ||
        bufferedEnd - currentTime < closestBuffer - currentTime)
    ) {
      closestBuffer = buffered.end(i);
    }
  }
  return closestBuffer;
};

const onCueChange = ({
  setSubtitlesCue,
  setSubtitlesCues,
  textTrack,
  useVTTPositioning
}: {
  setSubtitlesCue: PlayerStoreProperties["setSubtitlesCue"];
  setSubtitlesCues: PlayerStoreProperties["setSubtitlesCues"];
  textTrack: TextTrack;
  useVTTPositioning: boolean;
}) => {
  if (textTrack.activeCues && textTrack.activeCues.length > 0) {
    if (useVTTPositioning) {
      const cues = Array.from(textTrack.activeCues) as VTTCue[];
      const cueData = cues.map(cue => ({
        text: cue.text.trim(),
        line: cue.line,
        snapToLines: cue.snapToLines
      }));
      setSubtitlesCues(cueData);
      setSubtitlesCue("");
    } else {
      setSubtitlesCue(
        (Array.from(textTrack.activeCues) as VTTCue[])
          .reduce<string[]>((cueTexts, cue) => {
            const trimmed = cue.text.trim();
            cue.onexit = () => setSubtitlesCue("");
            if (!cueTexts.includes(trimmed)) {
              cueTexts.push(trimmed);
            }
            return cueTexts;
          }, [])
          .join("\n")
      );
      setSubtitlesCues([]);
    }
  } else {
    setSubtitlesCue("");
    setSubtitlesCues([]);
  }
};

export const resolveTextTrack = ({
  setSubtitles,
  setSubtitlesCue,
  setSubtitlesCues,
  textTrack,
  useVTTPositioning,
  usingServiceScheduleTracks = false
}: {
  setSubtitles: PlayerStoreProperties["setSubtitles"];
  setSubtitlesCue: PlayerStoreProperties["setSubtitlesCue"];
  setSubtitlesCues: PlayerStoreProperties["setSubtitlesCues"];
  textTrack: TextTrack;
  useVTTPositioning: boolean;
  usingServiceScheduleTracks?: boolean;
}): void => {
  if (
    (textTrack.kind === "subtitles" || textTrack.kind === "captions") &&
    textTrack.language
  ) {
    if (!textTrack.oncuechange) {
      textTrack.oncuechange = () => {
        onCueChange({
          setSubtitlesCue,
          setSubtitlesCues,
          textTrack,
          useVTTPositioning
        });
      };
    }
    if (!usingServiceScheduleTracks) {
      const newSubtitlesItem = parseSubtitlesItem(textTrack);

      setSubtitles(prevSubtitles =>
        prevSubtitles.some(e => e && e.label === newSubtitlesItem.label)
          ? prevSubtitles
          : [...prevSubtitles, newSubtitlesItem]
      );
    }
  }
};

export const resolveSelectedAudioTrack = (
  audioTracks: AudioTrack[],
  language: Language,
  setSelectedAudioTrackId: (selectedAudioTrackId: number) => void,
  userPreferredAudioTrack?: SelectedAudioTrack
): void => {
  if (audioTracks.length) {
    const matchingUserPreferredAudioTrack =
      userPreferredAudioTrack &&
      audioTracks.find(
        track =>
          track.language &&
          resolveTwoLetterCode(track.language) ===
            userPreferredAudioTrack.language &&
          (track.type === userPreferredAudioTrack.type ||
            (track.type === "AudioTrack" && !userPreferredAudioTrack.type))
      );

    if (matchingUserPreferredAudioTrack) {
      setSelectedAudioTrackId(matchingUserPreferredAudioTrack.id);
    } else {
      const defaultAudioTrackByUILanguage = audioTracks.find(
        track =>
          track.language &&
          resolveTwoLetterCode(track.language) ===
            resolveTwoLetterCode(language) &&
          (!track.type || track.type === "AudioTrack")
      );

      setSelectedAudioTrackId(
        defaultAudioTrackByUILanguage
          ? defaultAudioTrackByUILanguage.id
          : audioTracks[0].id
      );
    }
  } else {
    setSelectedAudioTrackId(-1);
  }
};

export const parseSubtitlesItem = (
  textTrack: TextTrack | Subtitles
): Subtitles => ({
  label: textTrack.label?.toLocaleLowerCase(),
  language: resolveTwoLetterCode(textTrack.language)
});

export const parseChromecastServiceScheduleSubtitlesItem = (
  textTrack: ChromecastServiceScheduleSubtitles
): Subtitles => ({
  label: textTrack.trackKey?.toLocaleLowerCase(),
  language: resolveTwoLetterCode(textTrack.language)
});

export const parseChromecastSubtitlesItem = (
  textTrack: chrome.cast.media.Track
): Subtitles => ({
  label: textTrack.name?.toLocaleLowerCase(),
  language: resolveTwoLetterCode(textTrack.language)
});
